import { useState } from 'react';
import { Power, PowerOff, Edit, Trash2 } from 'lucide-react';
import { IconDotsVertical } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import type { PromoOffer } from '../../types';
import { useUpdatePromoOfferStatus, useDeletePromoOffer } from '../../hooks/usePromoOfferManagement';

interface PromoOfferActionsProps {
  promoOffer: PromoOffer;
  onEdit?: (promoOffer: PromoOffer) => void;
}

export function PromoOfferActions({
  promoOffer,
  onEdit,
}: PromoOfferActionsProps) {
  const { hasPermission } = useAuth();
  const updateStatus = useUpdatePromoOfferStatus();
  const deletePromoOffer = useDeletePromoOffer();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleStatusToggle = () => {
    updateStatus.mutate({
      id: promoOffer.id,
      is_active: !promoOffer.is_active,
    });
  };

  const handleDelete = () => {
    deletePromoOffer.mutate(promoOffer.id);
    setShowDeleteDialog(false);
  };

  // Check if user has any relevant permissions
  const canUpdate = hasPermission('promo_management:update');
  const canDelete = hasPermission('promo_management:delete');

  // Don't render anything if user has no permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="data-[state=open]:bg-muted text-muted-foreground flex size-8 ml-auto"
            size="icon"
            data-testid={`promo-offer-actions-${promoOffer.id}`}
            disabled={updateStatus.isPending || deletePromoOffer.isPending}
          >
            <IconDotsVertical />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {canUpdate && onEdit && (
            <DropdownMenuItem
              onClick={() => onEdit(promoOffer)}
              data-testid={`edit-promo-offer-${promoOffer.id}`}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          
          {canUpdate && (
            <DropdownMenuItem
              onClick={handleStatusToggle}
              disabled={updateStatus.isPending}
              data-testid={`toggle-promo-offer-status-${promoOffer.id}`}
            >
              {promoOffer.is_active ? (
                <>
                  <PowerOff className="mr-2 h-4 w-4" />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Activate
                </>
              )}
            </DropdownMenuItem>
          )}
          
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive focus:text-destructive"
                data-testid={`delete-promo-offer-${promoOffer.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-promo-offer-dialog">
          <DialogHeader>
            <DialogTitle>Delete Promo Offer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this promo offer? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm font-medium">{promoOffer.short_description}</p>
            <p className="text-sm text-muted-foreground">
              From {promoOffer.venue_name}
            </p>
            {promoOffer.brand && (
              <p className="text-sm text-muted-foreground">
                Brand: {promoOffer.brand}
              </p>
            )}
            {promoOffer.claimed_count > 0 && (
              <p className="text-sm text-amber-600">
                Warning: This promo offer has {promoOffer.claimed_count} claim(s)
              </p>
            )}
            <p className="text-sm font-mono bg-muted px-2 py-1 rounded">
              Promo Code: {promoOffer.promo_code}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deletePromoOffer.isPending}
              data-testid="cancel-delete-promo-offer"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletePromoOffer.isPending}
              data-testid="confirm-delete-promo-offer"
            >
              {deletePromoOffer.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}